import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import TeeSheetDisplay from '../components/TeeSheetEditor/TeeSheetDisplay';
import OverflowQueue from '../components/TeeSheetEditor/OverflowQueue';
import ConflictPanel from '../components/TeeSheetEditor/ConflictPanel';
import RulesPanel from '../components/TeeSheetEditor/RulesPanel';
import PlayerCard from '../components/TeeSheetEditor/PlayerCard';
import { teeSheetAPI, playerAPI, tournamentAPI, paceDataAPI, dataAPI } from '../services/api';
import './TeeSheetEditor.css';

// Convert 12-hour format to 24-hour for HTML time input
const convertTo24Hour = (time12h) => {
  if (!time12h) return '07:03';
  const [time, period] = time12h.split(' ');
  let [hours, minutes] = time.split(':');
  hours = parseInt(hours);
  
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  
  return `${hours.toString().padStart(2, '0')}:${minutes}`;
};

// Convert 24-hour format to 12-hour for display
const convertTo12Hour = (time24h) => {
  if (!time24h) return '7:03 AM';
  let [hours, minutes] = time24h.split(':');
  hours = parseInt(hours);
  
  const period = hours >= 12 ? 'PM' : 'AM';
  if (hours > 12) hours -= 12;
  if (hours === 0) hours = 12;
  
  return `${hours}:${minutes} ${period}`;
};

function TeeSheetEditor() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { tournament, setTournament, refreshCounts, setOnEditTournament } = useOutletContext();
  const [teeSheet, setTeeSheet] = useState(null);
  const [players, setPlayers] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [overflowPlayers, setOverflowPlayers] = useState([]);
  
  // Find players who are in the roster but not assigned to any group or overflow
  const findUnassignedPlayers = () => {
    if (!players || !teeSheet) return [];
    
    // Get all players currently assigned to groups
    const assignedPlayerGhins = new Set();
    
    // Add players from groups
    if (teeSheet.groups) {
      teeSheet.groups.forEach(group => {
        if (group.players) {
          group.players.forEach(player => {
            assignedPlayerGhins.add(player.ghin);
          });
        }
      });
    }
    
    // Add players from overflow
    if (overflowPlayers) {
      overflowPlayers.forEach(player => {
        assignedPlayerGhins.add(player.ghin);
      });
    }
    
    // Find players not in either set
    const unassignedPlayers = players.filter(player => 
      !assignedPlayerGhins.has(player.ghin)
    );
    
    console.log('Found unassigned players:', unassignedPlayers.length, unassignedPlayers.map(p => p.name));
    return unassignedPlayers;
  };
  
  // Combined list of overflow + unassigned players for display
  const allOverflowPlayers = useMemo(() => {
    const unassignedPlayers = findUnassignedPlayers();
    const combined = [...overflowPlayers];
    
    // Add unassigned players that aren't already in overflow
    unassignedPlayers.forEach(player => {
      if (!combined.find(p => p.ghin === player.ghin)) {
        combined.push({
          ...player,
          isUnassigned: true,
          reason: 'Not assigned to any group'
        });
      }
    });
    
    return combined;
  }, [overflowPlayers, players, teeSheet]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meritData, setMeritData] = useState([]);
  const [paceData, setPaceData] = useState([]);
  const [requestFulfillment, setRequestFulfillment] = useState({ partner: {}, time: {} });
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [draggedPlayer, setDraggedPlayer] = useState(null);
  const [showVersionModal, setShowVersionModal] = useState(false);

  // Swap mode state
  const [swapMode, setSwapMode] = useState(null); // null | 'one' | 'two' | 'group'
  const [swapSelection, setSwapSelection] = useState(null); // { groupId, position, players: [...] }
  const [versionNotes, setVersionNotes] = useState('');
  const [isVersionBumping, setIsVersionBumping] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [feedbackByPlayer, setFeedbackByPlayer] = useState({});
  const [feedbackFilter, setFeedbackFilter] = useState({ status: 'all', type: 'all' });
  const [showTournamentEdit, setShowTournamentEdit] = useState(false);
  const [editingTournament, setEditingTournament] = useState({});

  useEffect(() => {
    if (tournamentId === 'demo') {
      loadDemoData();
    } else {
      loadTeeSheetData();
    }
  }, [tournamentId]);

  // Debug effect to track teeSheet state changes
  useEffect(() => {
    console.log('teeSheet state changed:', {
      teeSheet,
      hasGroups: teeSheet?.groups ? true : false,
      groupsLength: teeSheet?.groups?.length,
      groupsType: typeof teeSheet?.groups
    });
  }, [teeSheet]);

  // Set up the edit tournament handler
  useEffect(() => {
    if (setOnEditTournament) {
      setOnEditTournament(() => handleEditTournament);
    }
    
    // Cleanup function to clear the handler when component unmounts
    return () => {
      if (setOnEditTournament) {
        setOnEditTournament(null);
      }
    };
  }, [setOnEditTournament]);

  const loadDemoData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Demo players data
      const demoPlayers = [
        { id: 1, name: 'John Smith', handicapIndex: 8.5, ghin: '12345678', timeRequest: 'early', partnerRequest: 'Mike Johnson', merit: 45, pace: 120 },
        { id: 2, name: 'Mike Johnson', handicapIndex: 12.3, ghin: '23456789', timeRequest: null, partnerRequest: 'John Smith', merit: 78, pace: 85 },
        { id: 3, name: 'David Wilson', handicapIndex: 6.2, ghin: '34567890', timeRequest: 'late', partnerRequest: null, merit: 23, pace: 200 },
        { id: 4, name: 'Robert Brown', handicapIndex: 15.7, ghin: '45678901', timeRequest: null, partnerRequest: null, merit: 156, pace: 45 },
        { id: 5, name: 'James Davis', handicapIndex: 9.8, ghin: '56789012', timeRequest: 'early', partnerRequest: null, merit: 67, pace: 300 },
        { id: 6, name: 'William Miller', handicapIndex: 18.4, ghin: '67890123', timeRequest: null, partnerRequest: null, merit: 234, pace: 150 },
      ];

      const demoMeritData = [
        { ghin: '12345678', rank: 45 },
        { ghin: '23456789', rank: 78 },
        { ghin: '34567890', rank: 23 },
        { ghin: '45678901', rank: 156 },
        { ghin: '56789012', rank: 67 },
        { ghin: '67890123', rank: 234 }
      ];

      const demoPaceData = [
        { ghin: '12345678', rank: 120 },
        { ghin: '23456789', rank: 85 },
        { ghin: '34567890', rank: 200 },
        { ghin: '45678901', rank: 45 },
        { ghin: '56789012', rank: 300 },
        { ghin: '67890123', rank: 150 }
      ];

      setPlayers(demoPlayers);
      setMeritData(demoMeritData);
      setPaceData(demoPaceData);

      // Generate demo tee sheet
      const demoConfig = {
        startTime: '7:03 AM',
        interval: 9,
        maxGroupSize: 4,
        slowPlayerThreshold: 2
      };

      // For demo, directly call the generate API with demo data
      const response = await fetch('http://localhost:3001/api/tee-sheet/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          players: demoPlayers,
          config: demoConfig,
          meritData: demoMeritData,
          paceData: demoPaceData
        })
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const teeSheetResponse = await response.json();

      // Handle demo response structure (should match production)
      const demoTeeSheetData = teeSheetResponse.data || teeSheetResponse;
      setTeeSheet(demoTeeSheetData);
      setConflicts(Array.isArray(demoTeeSheetData.conflicts) ? demoTeeSheetData.conflicts : []);
      setOverflowPlayers(demoTeeSheetData.overflow || []);

    } catch (error) {
      console.error('Error loading demo data:', error);
      setError('Failed to load demo data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeeSheetData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Loading tee sheet data for tournament:', tournamentId);

      // Load tournament info
      console.log('Loading tournament info...');
      const tournamentResponse = await tournamentAPI.getById(tournamentId);
      console.log('Tournament loaded:', tournamentResponse.data.name);
      setTournament(tournamentResponse.data);

      // First, populate pace data for players from member records
      console.log('Populating pace data from member records...');
      try {
        const populateResponse = await playerAPI.populatePaceData(tournamentId);
        console.log('Pace data populated:', populateResponse.data.message);
      } catch (error) {
        console.error('Error populating pace data:', error);
      }

      // Load players (now with populated pace data)
      console.log('Loading players with pace data...');
      const playersResponse = await playerAPI.getAll(tournamentId);
      console.log('Players loaded:', playersResponse.data.length);
      setPlayers(playersResponse.data);

      // Extract merit data from player records (populated from Member records)
      const meritData = playersResponse.data.map(player => {
        if (player.orderOfMerit && player.orderOfMerit.effectiveRank) {
          return {
            ghin: player.ghin,
            oomRank: player.orderOfMerit.effectiveRank,
            playerName: player.name,
            effectivePoints: player.orderOfMerit.effectivePoints,
            currentYearRank: player.orderOfMerit.currentYearRank,
            currentYearPoints: player.orderOfMerit.currentYearPoints
          };
        } else {
          // Fallback for players without merit data
          return {
            ghin: player.ghin,
            oomRank: 999,
            playerName: player.name,
            effectivePoints: null,
            currentYearRank: null,
            currentYearPoints: null
          };
        }
      });

      console.log('Merit data extracted from players:', meritData.length, 'players with merit data');
      setMeritData(meritData);

      // Fetch pace data from PaceData collection via API
      try {
        const paceDataResponse = await dataAPI.getPaceData(tournamentId);
        const paceDataFromApi = paceDataResponse.data || [];
        console.log('Pace data fetched from API:', paceDataFromApi.length, 'records');

        // Create a map for quick lookup
        const paceDataMap = {};
        paceDataFromApi.forEach(p => {
          paceDataMap[p.ghin] = p;
        });

        // Build pace data array with fallback for players without pace data
        const paceData = playersResponse.data.map(player => {
          const apiPaceData = paceDataMap[player.ghin];
          if (apiPaceData) {
            return {
              ghin: player.ghin,
              rank: apiPaceData.rank || apiPaceData.popRank || 999,
              playerName: player.name,
              averageBehind: apiPaceData.averagePaceMinutes || null,
              totalRounds: apiPaceData.roundsPlayed || 0,
              paceRating: apiPaceData.rank <= 100 ? 'fast' : apiPaceData.rank <= 300 ? 'normal' : 'slow'
            };
          } else {
            // Fallback for players without pace data
            return {
              ghin: player.ghin,
              rank: 999,
              playerName: player.name,
              averageBehind: null,
              totalRounds: 0,
              paceRating: 'unknown'
            };
          }
        });

        console.log('Pace data processed:', paceData.filter(p => p.rank !== 999).length, 'players with actual pace data');
        setPaceData(paceData);
      } catch (paceError) {
        console.error('Failed to fetch pace data from API:', paceError);
        // Fallback: set all players to rank 999
        const paceData = playersResponse.data.map(player => ({
          ghin: player.ghin,
          rank: 999,
          playerName: player.name,
          averageBehind: null,
          totalRounds: 0,
          paceRating: 'unknown'
        }));
        setPaceData(paceData);
      }

      // Check if we have any players before generating tee sheet
      if (!playersResponse.data || playersResponse.data.length === 0) {
        console.log('No players found for tournament - setting empty tee sheet');
        setTeeSheet({
          groups: [],
          conflicts: [],
          overflow: []
        });
        setConflicts([]);
        setOverflowPlayers([]);
        return;
      }

      // Try to load existing saved tee sheet - do NOT auto-generate
      console.log('Checking for existing saved tee sheet for tournament:', tournamentId);
      try {
        const existingTeeSheetResponse = await teeSheetAPI.getByTournamentId(tournamentId);
        console.log('Found existing tee sheet:', existingTeeSheetResponse.data);

        if (existingTeeSheetResponse.data.success) {
          // Use existing saved tee sheet
          const teeSheetData = existingTeeSheetResponse.data.data;

          setTeeSheet({
            groups: teeSheetData.groups || [],
            conflicts: teeSheetData.conflicts || [],
            overflow: teeSheetData.overflowQueue || []
          });
          setOverflowPlayers(teeSheetData.overflowQueue || []);
          setConflicts(teeSheetData.conflicts || []);

          // Capture request fulfillment data
          if (teeSheetData.requestFulfillment) {
            setRequestFulfillment(teeSheetData.requestFulfillment);
          }

          console.log('Loaded existing tee sheet successfully');
        }
      } catch (existingError) {
        // No saved tee sheet exists - show empty state instead of auto-generating
        console.log('No existing tee sheet found - showing empty state:', existingError.response?.status);

        // Set empty tee sheet - user will use "Generate" button to create one
        setTeeSheet({
          groups: [],
          conflicts: [],
          overflow: [],
          totalGroups: 0,
          totalPlayers: 0,
          isEmpty: true  // Flag to show empty state UI
        });
        setConflicts([]);

        // Put all players in overflow until tee sheet is generated
        setOverflowPlayers(playersResponse.data || []);
      }

      // Load feedback data
      await loadFeedbackData();

    } catch (err) {
      console.error('Error loading tee sheet data:', err);
      setError('Failed to load tee sheet data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFeedbackData = async () => {
    try {
      console.log('Loading feedback data for tournament:', tournamentId);
      const feedbackResponse = await tournamentAPI.getTournamentFeedback(tournamentId);
      
      console.log('Feedback API response:', feedbackResponse.data);
      
      if (feedbackResponse.data.success) {
        console.log('Setting feedback data:', {
          feedbackCount: feedbackResponse.data.feedback.length,
          playerCount: Object.keys(feedbackResponse.data.feedbackByPlayer).length
        });
        setFeedback(feedbackResponse.data.feedback);
        setFeedbackByPlayer(feedbackResponse.data.feedbackByPlayer);
        console.log('Feedback loaded:', feedbackResponse.data.summary);
      } else {
        console.warn('Feedback API returned success: false');
      }
    } catch (err) {
      console.warn('Failed to load feedback data:', err);
      console.warn('Error response:', err.response?.data);
      // Don't throw error - feedback is not critical for tee sheet functionality
    }
  };

  const updateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      await tournamentAPI.updateFeedbackStatus(feedbackId, newStatus);
      // Reload feedback data to get updated counts
      await loadFeedbackData();
    } catch (err) {
      console.error('Failed to update feedback status:', err);
      alert('Failed to update feedback status. Please try again.');
    }
  };

  const bulkUpdateFeedbackStatus = async (feedbackIds, newStatus) => {
    try {
      await tournamentAPI.bulkUpdateFeedbackStatus(feedbackIds, newStatus);
      // Reload feedback data to get updated counts
      await loadFeedbackData();
    } catch (err) {
      console.error('Failed to bulk update feedback status:', err);
      alert('Failed to bulk update feedback status. Please try again.');
    }
  };

  const handleEditTournament = () => {
    setEditingTournament({
      name: tournament?.name || '',
      startTime: tournament?.startTime || '7:03 AM',
      status: tournament?.status || 'draft',
      feedbackDeadline: tournament?.feedbackDeadline || '',
      feedbackGracePeriodMinutes: tournament?.feedbackGracePeriodMinutes || 15
    });
    setShowTournamentEdit(true);
  };

  const handleSaveTournamentSettings = async () => {
    try {
      const updateData = {
        name: editingTournament.name?.trim() || tournament?.name,
        startTime: editingTournament.startTime,
        status: editingTournament.status,
        feedbackDeadline: editingTournament.feedbackDeadline || null,
        feedbackGracePeriodMinutes: editingTournament.feedbackGracePeriodMinutes || 15
      };

      await tournamentAPI.update(tournamentId, updateData);
      
      // Update local tournament state
      setTournament(prev => ({
        ...prev,
        ...updateData
      }));
      
      setShowTournamentEdit(false);
      setUnsavedChanges(false);
      
    } catch (err) {
      console.error('Failed to update tournament settings:', err);
      alert('Failed to update tournament settings. Please try again.');
    }
  };

  const handleCancelTournamentEdit = () => {
    setShowTournamentEdit(false);
    setEditingTournament({});
  };

  const handlePlayerMove = async (playerId, sourceGroupId, targetGroupId, targetPosition) => {
    try {
      setUnsavedChanges(true);
      
      // Find the player
      const player = findPlayerById(playerId);
      if (!player) return;

      // Create new tee sheet with the move
      const newTeeSheet = { ...teeSheet };
      const newGroups = [...newTeeSheet.groups];

      // Remove player from source
      if (sourceGroupId === 'overflow') {
        setOverflowPlayers(prev => prev.filter(p => p.ghin !== playerId));
      } else {
        const sourceGroup = newGroups.find(g => g.id === sourceGroupId);
        if (sourceGroup) {
          sourceGroup.players = sourceGroup.players.filter(p => p.ghin !== playerId);
        }
      }

      // Add player to target
      if (targetGroupId === 'overflow') {
        setOverflowPlayers(prev => [...prev, player]);
      } else {
        const targetGroup = newGroups.find(g => g.id === targetGroupId);
        if (targetGroup) {
          // Insert at specific position or append
          if (targetPosition !== undefined) {
            targetGroup.players.splice(targetPosition, 0, player);
          } else {
            targetGroup.players.push(player);
          }
        }
      }

      // Update manual override flag  
      const updateData = {
        manualOverride: {
          isOverridden: true,
          reason: 'Manual placement via drag and drop'
        }
      };
      
      // Only set groupNumber and position for actual groups (not overflow)
      if (targetGroupId !== 'overflow') {
        updateData.manualOverride.groupNumber = targetGroupId;
        if (targetPosition !== undefined) {
          updateData.manualOverride.position = targetPosition + 1; // Convert to 1-based
        }
      }
      
      await playerAPI.update(player._id, updateData);

      setTeeSheet({ ...newTeeSheet, groups: newGroups });
      setUnsavedChanges(true);

    } catch (err) {
      console.error('Error moving player:', err);
      console.error('Error details:', {
        playerId,
        sourceGroupId,
        targetGroupId,
        targetPosition,
        updateData: err.response?.data || err.message
      });
      setError(`Failed to move player: ${err.response?.data?.error || err.message}`);
    }
  };

  // Generate tee sheet using the rules-based system
  const handleGenerateTeeSheet = async () => {
    try {
      setIsGenerating(true);
      setError(null);

      // Call the rules-based generation API
      const response = await teeSheetAPI.generateFromRules(tournamentId);

      const teeSheetData = response.data.data;

      if (teeSheetData && teeSheetData.groups) {
        setTeeSheet(teeSheetData);
        setConflicts(Array.isArray(teeSheetData.conflicts) ? teeSheetData.conflicts : []);
        setOverflowPlayers(teeSheetData.overflow || []);

        // Capture request fulfillment data
        if (teeSheetData.requestFulfillment) {
          setRequestFulfillment(teeSheetData.requestFulfillment);
        }

        // Refresh tab counts
        if (refreshCounts) {
          refreshCounts();
        }

        setUnsavedChanges(true);
      } else {
        console.error('Invalid tee sheet data received:', teeSheetData);
        setError('Failed to generate tee sheet - invalid data received');
      }
    } catch (err) {
      console.error('Error generating tee sheet:', err);
      setError(`Failed to generate tee sheet: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const findPlayerById = (playerId) => {
    // Check in groups
    for (const group of teeSheet?.groups || []) {
      const player = group.players.find(p => p.ghin === playerId);
      if (player) return player;
    }
    // Check in overflow (including unassigned)
    return allOverflowPlayers.find(p => p.ghin === playerId);
  };

  const findPlayerSource = (playerId) => {
    // Check groups first
    for (const group of teeSheet?.groups || []) {
      const playerIndex = group.players.findIndex(p => p.ghin === playerId);
      if (playerIndex !== -1) {
        return {
          groupId: group.id,
          position: playerIndex
        };
      }
    }
    
    // Check overflow players (including unassigned)
    const overflowPlayerIndex = (allOverflowPlayers || []).findIndex(p => p.ghin === playerId);
    if (overflowPlayerIndex !== -1) {
      return {
        groupId: 'overflow',
        position: overflowPlayerIndex
      };
    }
    
    return null;
  };

  const parseDropZone = (overId) => {
    if (typeof overId === 'string') {
      // Handle overflow drop zones
      if (overId === 'overflow' || overId === 'overflow-drop-zone') {
        return {
          groupId: 'overflow',
          position: undefined
        };
      }
      // Handle group drop zones
      if (overId.startsWith('group-')) {
        return {
          groupId: parseInt(overId.replace('group-', '')),
          position: undefined // Append to end
        };
      }
      // Handle position-specific drop zones
      if (overId.startsWith('position-')) {
        const parts = overId.split('-');
        return {
          groupId: parseInt(parts[1]),
          position: parseInt(parts[2])
        };
      }
    }
    return null;
  };

  // Swap mode handlers
  const handleSwapModeToggle = (mode) => {
    if (swapMode === mode) {
      // Toggle off if clicking same mode
      setSwapMode(null);
      setSwapSelection(null);
    } else {
      setSwapMode(mode);
      setSwapSelection(null);
    }
  };

  const handleSwapSelect = (groupId, position) => {
    if (!swapMode) return;

    const group = teeSheet?.groups?.find(g => g.id === groupId);
    if (!group) return;

    // Determine which players to select based on swap mode
    let selectedPlayers = [];

    if (swapMode === 'one') {
      // Select just the player at this position
      const player = group.players[position];
      if (player) {
        selectedPlayers = [{ ...player, groupId, position }];
      }
    } else if (swapMode === 'two') {
      // Select player and the one to their right (next position)
      const player1 = group.players[position];
      const player2 = group.players[position + 1];
      if (player1) {
        selectedPlayers.push({ ...player1, groupId, position });
      }
      if (player2) {
        selectedPlayers.push({ ...player2, groupId, position: position + 1 });
      }
    } else if (swapMode === 'group') {
      // Select entire group
      selectedPlayers = group.players.map((player, idx) => ({
        ...player,
        groupId,
        position: idx
      }));
    }

    // If no selection yet, require at least one player (can't select empty first)
    if (!swapSelection) {
      if (selectedPlayers.length === 0) return;
      setSwapSelection({
        groupId,
        position,
        players: selectedPlayers
      });
      return;
    }

    // If we have a selection, perform the swap (empty target is allowed)
    performSwap(swapSelection, { groupId, position, players: selectedPlayers });
  };

  const performSwap = async (source, target) => {
    try {
      setUnsavedChanges(true);

      const newTeeSheet = { ...teeSheet };
      const newGroups = [...newTeeSheet.groups];

      if (swapMode === 'group') {
        // Swap entire groups - just swap the players arrays
        const sourceGroup = newGroups.find(g => g.id === source.groupId);
        const targetGroup = newGroups.find(g => g.id === target.groupId);

        if (sourceGroup && targetGroup && source.groupId !== target.groupId) {
          const tempPlayers = [...sourceGroup.players];
          sourceGroup.players = [...targetGroup.players];
          targetGroup.players = tempPlayers;
        }
      } else if (swapMode === 'two') {
        // Swap pairs
        const sourceGroup = newGroups.find(g => g.id === source.groupId);
        const targetGroup = newGroups.find(g => g.id === target.groupId);

        if (sourceGroup && targetGroup) {
          // Get source players (at positions source.position and source.position + 1)
          const sourcePlayer1 = sourceGroup.players[source.position];
          const sourcePlayer2 = sourceGroup.players[source.position + 1];

          // Get target players
          const targetPlayer1 = targetGroup.players[target.position];
          const targetPlayer2 = targetGroup.players[target.position + 1];

          // Perform the swap
          if (sourcePlayer1) sourceGroup.players[source.position] = targetPlayer1 || null;
          if (source.position + 1 < sourceGroup.players.length || targetPlayer2) {
            sourceGroup.players[source.position + 1] = targetPlayer2 || null;
          }
          if (targetPlayer1 !== undefined) targetGroup.players[target.position] = sourcePlayer1 || null;
          if (target.position + 1 < targetGroup.players.length || sourcePlayer2) {
            targetGroup.players[target.position + 1] = sourcePlayer2 || null;
          }

          // Clean up null values
          sourceGroup.players = sourceGroup.players.filter(p => p !== null);
          targetGroup.players = targetGroup.players.filter(p => p !== null);
        }
      } else {
        // Swap single players
        const sourceGroup = newGroups.find(g => g.id === source.groupId);
        const targetGroup = newGroups.find(g => g.id === target.groupId);

        if (sourceGroup && targetGroup) {
          const sourcePlayer = sourceGroup.players[source.position];
          const targetPlayer = targetGroup.players[target.position];

          // Swap the players
          if (sourcePlayer && targetPlayer) {
            sourceGroup.players[source.position] = targetPlayer;
            targetGroup.players[target.position] = sourcePlayer;
          } else if (sourcePlayer && !targetPlayer) {
            // Move source to empty target slot
            targetGroup.players[target.position] = sourcePlayer;
            sourceGroup.players.splice(source.position, 1);
          } else if (!sourcePlayer && targetPlayer) {
            // Move target to empty source slot
            sourceGroup.players[source.position] = targetPlayer;
            targetGroup.players.splice(target.position, 1);
          }
        }
      }

      setTeeSheet({ ...newTeeSheet, groups: newGroups });
      setSwapSelection(null);
      setUnsavedChanges(true);

    } catch (err) {
      console.error('Error performing swap:', err);
      setError(`Failed to swap: ${err.message}`);
    }
  };

  const cancelSwapSelection = () => {
    setSwapSelection(null);
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    
    // Find the player being dragged
    const player = findPlayerById(active.id);
    setDraggedPlayer(player);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) {
      setActiveId(null);
      setDraggedPlayer(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    // Parse drop zone information
    const dropInfo = parseDropZone(overId);
    
    if (dropInfo && activeId !== overId) {
      const sourceInfo = findPlayerSource(activeId);
      
      if (sourceInfo) {
        handlePlayerMove(
          activeId,
          sourceInfo.groupId,
          dropInfo.groupId,
          dropInfo.position
        );
      }
    }

    setActiveId(null);
    setDraggedPlayer(null);
  };

  const getPlayerMeritRank = (ghin) => {
    const merit = meritData.find(m => m.ghin === ghin);
    return merit?.oomRank || 'N/A';
  };

  const getPlayerPaceRank = (ghin) => {
    const pace = paceData.find(p => p.ghin === ghin);
    // Debug logging for any call
    console.log('getPlayerPaceRank called:', {
      ghin,
      paceDataLength: paceData?.length,
      foundPace: !!pace,
      rank: pace?.rank,
      returning: pace?.rank || 'N/A'
    });
    return pace?.rank || 'N/A';
  };

  const calculateTeeTime = (groupIndex, startTime, interval) => {
    // Parse start time (assuming format like "8:00 AM")
    const [time, period] = startTime.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    let startHour = hours;
    if (period === 'PM' && hours !== 12) {
      startHour += 12;
    } else if (period === 'AM' && hours === 12) {
      startHour = 0;
    }

    // Calculate tee time by adding interval * groupIndex minutes
    const totalMinutes = (startHour * 60) + minutes + (interval * groupIndex);
    const teeHour = Math.floor(totalMinutes / 60) % 24;
    const teeMinute = totalMinutes % 60;

    // Format back to readable time
    const displayHour = teeHour === 0 ? 12 : (teeHour > 12 ? teeHour - 12 : teeHour);
    const displayPeriod = teeHour < 12 ? 'AM' : 'PM';
    const formattedMinute = teeMinute.toString().padStart(2, '0');
    
    return `${displayHour}:${formattedMinute} ${displayPeriod}`;
  };

  const recalculateTeeTimesForGroups = (groups, config) => {
    return groups.map((group, index) => ({
      ...group,
      id: index + 1,
      teeTime: calculateTeeTime(
        index,
        config.startTime || '7:03 AM',
        config.interval || 9
      )
    }));
  };

  const handleMoveGroupUp = (groupId) => {
    const groupIndex = teeSheet.groups.findIndex(g => g.id === groupId);
    if (groupIndex <= 0) return;

    const newGroups = [...teeSheet.groups];
    [newGroups[groupIndex - 1], newGroups[groupIndex]] = [newGroups[groupIndex], newGroups[groupIndex - 1]];
    
    // Recalculate times and IDs
    const updatedGroups = recalculateTeeTimesForGroups(newGroups, teeSheet.config || {});
    
    setTeeSheet(prev => ({ ...prev, groups: updatedGroups }));
    setUnsavedChanges(true);
  };

  const handleMoveGroupDown = (groupId) => {
    const groupIndex = teeSheet.groups.findIndex(g => g.id === groupId);
    if (groupIndex >= teeSheet.groups.length - 1) return;

    const newGroups = [...teeSheet.groups];
    [newGroups[groupIndex], newGroups[groupIndex + 1]] = [newGroups[groupIndex + 1], newGroups[groupIndex]];
    
    // Recalculate times and IDs
    const updatedGroups = recalculateTeeTimesForGroups(newGroups, teeSheet.config || {});
    
    setTeeSheet(prev => ({ ...prev, groups: updatedGroups }));
    setUnsavedChanges(true);
  };

  const handleDeleteGroup = (groupId) => {
    const group = teeSheet.groups.find(g => g.id === groupId);
    if (!group || group.players.length > 0) return;

    const newGroups = teeSheet.groups.filter(g => g.id !== groupId);
    
    // Recalculate times and IDs
    const updatedGroups = recalculateTeeTimesForGroups(newGroups, teeSheet.config || {});
    
    setTeeSheet(prev => ({ 
      ...prev, 
      groups: updatedGroups,
      totalGroups: updatedGroups.length
    }));
    setUnsavedChanges(true);
  };

  const handleAutoFixConflicts = async (conflictsToFix) => {
    try {
      let updatedGroups = [...teeSheet.groups];
      let updatedPlayers = [...players];
      let hasChanges = false;

      for (const conflict of conflictsToFix) {
        switch (conflict.type) {
          case 'duplicate_player':
            // Remove player from all groups except the first one
            const playerGhin = conflict.player.ghin;
            let keepFirstGroup = true;
            
            updatedGroups = updatedGroups.map(group => ({
              ...group,
              players: group.players.filter(p => {
                if (p.ghin === playerGhin) {
                  if (keepFirstGroup) {
                    keepFirstGroup = false;
                    return true; // Keep in first group
                  }
                  hasChanges = true;
                  return false; // Remove from subsequent groups
                }
                return true;
              })
            }));
            break;

          case 'unmatched_partner_request':
            // Clear the invalid partner request
            const playerToUpdate = updatedPlayers.find(p => p.ghin === conflict.player.ghin);
            if (playerToUpdate) {
              playerToUpdate.parsedPartnerRequest = '';
              playerToUpdate.rawPartnerRequest = '';
              await handlePlayerUpdate(playerToUpdate);
              hasChanges = true;
            }
            break;

          case 'pace_overflow':
            // Try to place overflow players in groups with available spots
            for (const overflowPlayer of conflict.players) {
              for (let groupIndex = 0; groupIndex < updatedGroups.length; groupIndex++) {
                if (updatedGroups[groupIndex].players.length < 4) {
                  updatedGroups[groupIndex].players.push(overflowPlayer);
                  hasChanges = true;
                  break;
                }
              }
            }
            break;

          case 'pace_violation':
            // Move some slow players to groups with fewer slow players
            const groupIndex = conflict.groupIndex;
            const slowPlayersToMove = conflict.slowPlayers.slice(conflict.threshold);
            
            for (const slowPlayer of slowPlayersToMove) {
              // Remove from current group
              updatedGroups[groupIndex].players = updatedGroups[groupIndex].players.filter(
                p => p.ghin !== slowPlayer.ghin
              );
              
              // Find group with fewest slow players
              let bestGroupIndex = 0;
              let minSlowPlayers = Infinity;
              
              for (let i = 0; i < updatedGroups.length; i++) {
                if (i === groupIndex || updatedGroups[i].players.length >= 4) continue;
                
                const slowCount = updatedGroups[i].players.filter(p => 
                  players.find(player => player.ghin === p.ghin)?.pace === 'slow'
                ).length;
                
                if (slowCount < minSlowPlayers) {
                  minSlowPlayers = slowCount;
                  bestGroupIndex = i;
                }
              }
              
              if (updatedGroups[bestGroupIndex].players.length < 4) {
                updatedGroups[bestGroupIndex].players.push(slowPlayer);
                hasChanges = true;
              }
            }
            break;
        }
      }

      if (hasChanges) {
        // Recalculate times and IDs after auto-fix changes
        const finalGroups = recalculateTeeTimesForGroups(updatedGroups, teeSheet.config || {});
        setTeeSheet(prev => ({
          ...prev,
          groups: finalGroups,
          totalGroups: finalGroups.length
        }));

        console.log(`Auto-fixed ${conflictsToFix.length} conflicts`);
      }
    } catch (err) {
      console.error('Error auto-fixing conflicts:', err);
      setError('Failed to auto-fix conflicts. Please try again.');
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      // Ensure each player has position information preserved
      const groupsWithPositions = teeSheet.groups.map(group => ({
        ...group,
        players: group.players.map((player, index) => ({
          ...player,
          position: player.position || (index + 1)
        }))
      }));
      
      // Get all players that are currently in groups
      const playersInGroups = new Set();
      groupsWithPositions.forEach(group => {
        group.players.forEach(player => {
          playersInGroups.add(player.ghin);
        });
      });
      
      // Filter overflow players - only keep those NOT in groups
      const filteredOverflowPlayers = overflowPlayers.filter(player => 
        !playersInGroups.has(player.ghin)
      );
      
      const saveData = {
        groups: groupsWithPositions,
        overflowPlayers: filteredOverflowPlayers,
        config: teeSheet.config || {}
      };
      
      console.log('Saving tee sheet with position data:', saveData);
      const response = await teeSheetAPI.save(tournamentId, saveData);
      
      if (response.data.success) {
        setUnsavedChanges(false);
        console.log('Tee sheet saved successfully:', response.data.data);
      }
    } catch (err) {
      console.error('Error saving tee sheet:', err);
      setError('Failed to save tee sheet. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInsertTeeTime = async (position) => {
    try {
      if (!teeSheet || !teeSheet.groups) {
        setError('No tee sheet available to insert tee time into.');
        return;
      }

      // Get tournament config for tee time calculation
      const config = teeSheet.config || {
        startTime: '7:03 AM',
        interval: 9,
        maxGroupSize: 4
      };

      // Create new empty group
      const newGroup = {
        id: 0, // Will be updated after insertion
        players: [],
        teeTime: '', // Will be calculated after insertion
        paceStatus: 'valid',
        slowPlayerCount: 0
      };

      // Insert the new group at the specified position
      const newGroups = [...teeSheet.groups];
      newGroups.splice(position, 0, newGroup);

      // Recalculate all group IDs and tee times
      const updatedGroups = recalculateTeeTimesForGroups(newGroups, config);

      setTeeSheet(prev => ({
        ...prev,
        groups: updatedGroups,
        totalGroups: updatedGroups.length
      }));
      
      setUnsavedChanges(true);
      console.log('Inserted new tee time at position:', position);
    } catch (err) {
      console.error('Error inserting tee time:', err);
      setError('Failed to insert tee time. Please try again.');
    }
  };

  const handleExport = async (format = 'pdf') => {
    try {
      const response = await teeSheetAPI.export(teeSheet.id, format);
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `tee-sheet-${tournamentId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err);
      setError('Failed to export tee sheet. Please try again.');
    }
  };




  const handlePlayerUpdate = async (updatedPlayer) => {
    try {
      // Handle special reload case for LLM reprocessing
      if (updatedPlayer?.type === 'reload_all') {
        console.log('Reloading all players after LLM processing...');
        const playersResponse = await playerAPI.getAll(tournamentId);
        setPlayers(playersResponse.data);
        console.log('Players reloaded successfully:', playersResponse.data.length);
        return;
      }

      // Check if this update is already saved (from AI prediction)
      if (!updatedPlayer._alreadySaved) {
        // Normal single player update - make API call
        await playerAPI.update(updatedPlayer._id, {
          timePreference: updatedPlayer.timePreference,
          rawPartnerRequest: updatedPlayer.rawPartnerRequest,
          notes: updatedPlayer.notes,
          parsedTimePreference: updatedPlayer.parsedTimePreference,
          parsedPartnerPreference: updatedPlayer.parsedPartnerPreference,
          parsedPairingPreference: updatedPlayer.parsedPairingPreference,
          originalPairingRequest: updatedPlayer.originalPairingRequest
        });
      } else {
        console.log('Skipping API update for already saved player');
      }

      // Update local players state (remove the marker)
      const cleanPlayer = { ...updatedPlayer };
      delete cleanPlayer._alreadySaved;
      setPlayers(prev => 
        prev.map(p => p._id === updatedPlayer._id ? cleanPlayer : p)
      );

      console.log('Player updated successfully:', updatedPlayer.name);
    } catch (error) {
      console.error('Error updating player:', error);
      throw error; // Re-throw so the component can handle it
    }
  };

  const handleAddToTeeSheet = (player) => {
    // Add the unassigned player to the overflow queue so they can be dragged to groups
    setOverflowPlayers(prev => {
      // Check if player is already in overflow
      const exists = prev.some(p => p.ghin === player.ghin);
      if (exists) {
        return prev;
      }
      // Add to overflow queue
      return [...prev, { ...player, isUnassigned: false }];
    });
  };

  const handleVersionBump = () => {
    setShowVersionModal(true);
    setVersionNotes('');
  };

  const handleVersionBumpConfirm = async () => {
    try {
      setIsVersionBumping(true);
      
      const response = await tournamentAPI.bumpVersion(tournamentId, versionNotes);
      
      // Update tournament state with new version
      setTournament(prev => ({
        ...prev,
        version: response.data.currentVersion
      }));
      
      console.log(`Version bumped successfully: v${response.data.previousVersion} → v${response.data.currentVersion}`);
      
      // Show success message or notification here if needed
      alert(`Successfully created snapshot v${response.data.previousVersion} and bumped to v${response.data.currentVersion}`);
      
      setShowVersionModal(false);
      setVersionNotes('');
      
      // Reload tee sheet data to refresh the state after version bump
      console.log('Reloading tee sheet data after version bump...');
      await loadTeeSheetData();
      
    } catch (error) {
      console.error('Version bump failed:', error);
      alert(error.response?.data?.error || 'Failed to bump version');
    } finally {
      setIsVersionBumping(false);
    }
  };

  const handleVersionBumpCancel = () => {
    setShowVersionModal(false);
    setVersionNotes('');
  };

  if (isLoading) {
    return (
      <div className="tee-sheet-editor loading">
        <div className="loading-spinner">Loading tee sheet...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tee-sheet-editor error">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={loadTeeSheetData}>Try Again</button>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
          <button onClick={() => navigate(`/tournament/${tournamentId}/import`)}>Import Data</button>
        </div>
      </div>
    );
  }

  return (
    <div className="tee-sheet-editor">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {/* Content Area - Two Columns */}
        <div className="editor-content">
          {/* Left Column - Tee Sheet */}
          <div className="tee-sheet-column">
            <TeeSheetDisplay
              teeSheet={teeSheet}
              conflicts={Array.isArray(conflicts) ? conflicts : []}
              meritData={meritData}
              paceData={paceData}
              overflowPlayers={allOverflowPlayers}
              activeId={activeId}
              onMoveGroupUp={handleMoveGroupUp}
              onMoveGroupDown={handleMoveGroupDown}
              onDeleteGroup={handleDeleteGroup}
              onInsertTeeTime={handleInsertTeeTime}
              feedbackByPlayer={feedbackByPlayer}
              requestFulfillment={requestFulfillment}
              tournament={tournament}
              unsavedChanges={unsavedChanges}
              onVersionBump={handleVersionBump}
              onExport={handleExport}
              onSave={handleSave}
              conflictCount={Array.isArray(conflicts) ? conflicts.length : 0}
              feedbackCount={feedback.length}
              onFeedbackClick={(playerGhin) => {
                // Feedback is now handled via tabs - navigate to feedback tab
                // TODO: Filter feedback to show only this player's feedback
              }}
              swapMode={swapMode}
              swapSelection={swapSelection}
              onSwapModeToggle={handleSwapModeToggle}
              onSwapSelect={handleSwapSelect}
              onCancelSwap={cancelSwapSelection}
              onGenerateTeeSheet={handleGenerateTeeSheet}
              isGenerating={isGenerating}
            />
          </div>

          {/* Right Column - Rules, Conflicts and Overflow */}
          <div className="management-column">
            <RulesPanel
              tournamentId={tournament?._id}
              onRuleApply={(rule) => {
                // Rule applied - tee sheet will be regenerated separately
                console.log('Rule applied:', rule);
              }}
              onRuleDelete={(ruleId) => {
                // Rule deleted - tee sheet will be regenerated separately
                console.log('Rule deleted:', ruleId);
              }}
              onConflictSelect={(ruleConflicts) => {
                // Could scroll to or highlight related conflicts
                console.log('Rule conflicts selected:', ruleConflicts);
              }}
            />

            <ConflictPanel
              conflicts={Array.isArray(conflicts) ? conflicts : []}
              onConflictResolve={(conflictIndex) => {
                setConflicts(prev => Array.isArray(prev) ? prev.filter((c, index) => index !== conflictIndex) : []);
              }}
              onAutoFixConflicts={handleAutoFixConflicts}
            />

            <OverflowQueue
              players={allOverflowPlayers}
              getPlayerMeritRank={getPlayerMeritRank}
              getPlayerPaceRank={getPlayerPaceRank}
              requestFulfillment={requestFulfillment}
            />
          </div>
        </div>

        <DragOverlay>
          {activeId && draggedPlayer ? (
            <PlayerCard
              player={draggedPlayer}
              meritRank={getPlayerMeritRank(draggedPlayer.ghin)}
              paceRank={getPlayerPaceRank(draggedPlayer.ghin)}
              isDragging={true}
            />
          ) : null}
        </DragOverlay>
      </DndContext>


      {/* Version Bump Modal */}
      <Dialog open={showVersionModal} onOpenChange={(open) => !open && handleVersionBumpCancel()}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Bump Version</DialogTitle>
            <DialogDescription>
              This will create a snapshot of v{tournament?.version || 1} and bump to v{(tournament?.version || 1) + 1}.
              The current tee sheet state will be automatically saved.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="version-notes" className="text-sm font-medium">
                Notes (optional)
              </label>
              <Textarea
                id="version-notes"
                value={versionNotes}
                onChange={(e) => setVersionNotes(e.target.value)}
                placeholder="Add notes about this version..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleVersionBumpCancel}
              disabled={isVersionBumping}
            >
              Cancel
            </Button>
            <Button
              onClick={handleVersionBumpConfirm}
              disabled={isVersionBumping}
            >
              {isVersionBumping ? 'Bumping...' : `Create v${(tournament?.version || 1) + 1}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tournament Settings Modal */}
      <Dialog open={showTournamentEdit} onOpenChange={(open) => !open && handleCancelTournamentEdit()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Tournament Settings</DialogTitle>
            <DialogDescription>
              Configure tournament details and feedback settings.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="tournament-name" className="text-sm font-medium">
                Tournament Name
              </label>
              <Input
                type="text"
                id="tournament-name"
                value={editingTournament.name || ''}
                onChange={(e) => setEditingTournament(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter tournament name"
                required
              />
            </div>

            <div className="grid gap-2">
              <label htmlFor="start-time" className="text-sm font-medium">
                Start Time
              </label>
              <Input
                type="time"
                id="start-time"
                value={convertTo24Hour(editingTournament.startTime || '7:03 AM')}
                onChange={(e) => setEditingTournament(prev => ({
                  ...prev,
                  startTime: convertTo12Hour(e.target.value)
                }))}
              />
              <p className="text-sm text-muted-foreground">First tee time of the tournament</p>
            </div>

            <div className="grid gap-2">
              <label htmlFor="tournament-status" className="text-sm font-medium">
                Status
              </label>
              <Select
                value={editingTournament.status}
                onValueChange={(value) => setEditingTournament(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="setup">Setup</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="published-for-feedback">Published for Feedback</SelectItem>
                  <SelectItem value="finalized">Finalized</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <label htmlFor="feedback-deadline" className="text-sm font-medium">
                Feedback Deadline
              </label>
              <Input
                type="datetime-local"
                id="feedback-deadline"
                value={editingTournament.feedbackDeadline ?
                  new Date(editingTournament.feedbackDeadline).toISOString().slice(0, 16) : ''}
                onChange={(e) => setEditingTournament(prev => ({
                  ...prev,
                  feedbackDeadline: e.target.value ? new Date(e.target.value).toISOString() : ''
                }))}
              />
              <p className="text-sm text-muted-foreground">Leave empty for no deadline</p>
            </div>

            <div className="grid gap-2">
              <label htmlFor="grace-period" className="text-sm font-medium">
                Grace Period (minutes)
              </label>
              <Input
                type="number"
                id="grace-period"
                min="0"
                max="60"
                value={editingTournament.feedbackGracePeriodMinutes || 15}
                onChange={(e) => setEditingTournament(prev => ({
                  ...prev,
                  feedbackGracePeriodMinutes: parseInt(e.target.value) || 15
                }))}
              />
              <p className="text-sm text-muted-foreground">Extra time allowed after deadline (0-60 minutes)</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelTournamentEdit}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveTournamentSettings}
            >
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default TeeSheetEditor;